import { Autocomplete, Box, InputLabel, TextField } from '@mui/material';
import { Controller, FieldValues } from 'react-hook-form';

import { BaseInputProps } from './GetFormInput';

// Flexible option shape supporting multiple sources across the app.
interface OptionType {
    color?: string;
    description?: string | null;
    id?: string | number | null;
    label?: string;
    name?: string;
    value?: string | number | null;
}

export function FormAutocomplete<T extends FieldValues>({
    control,
    disabled,
    fullWidth,
    helperText,
    label,
    options,
    required,
    small,
    value,
}: Readonly<BaseInputProps<T>>) {
    const deriveLabel = (option: OptionType | string | null): string => {
        if (option == null) {
            return '';
        }
        if (typeof option === 'string') {
            return option;
        }

        const baseId =
            (option.id != null ? String(option.id) : '') ||
            (option.value != null ? String(option.value) : '') ||
            option.label ||
            option.name ||
            '';
        const baseName = option.name || option.label || '';
        const description = option.description || null;

        if (value === 'instance' || value === 'customerInstance') {
            return baseName || baseId;
        }
        if (value === 'tag_id' || value === 'tag_ids' || value === 'tag_type_id') {
            if (option.label) {
                return option.label;
            }

            const truncatedId = baseId.length > 25 ? baseId.slice(0, 25) + '...' : baseId;
            if (description) {
                return `${description} - ${truncatedId}`;
            }

            return baseName || truncatedId;
        }
        return baseId;
    };

    return (
        <Controller
            control={control}
            name={value}
            render={({ field: { onBlur, onChange, ref, value: inputValue }, fieldState: { error } }) => {
                return (
                    <Box>
                        <InputLabel sx={{ color: 'black', fontWeight: 'medium', mb: 0.5 }}>{label}</InputLabel>
                        <Autocomplete
                            disabled={disabled}
                            getOptionLabel={(option: OptionType | string) => deriveLabel(option)}
                            isOptionEqualToValue={(option, selected) => {
                                const optionKey =
                                    (option as OptionType).id ??
                                    (option as OptionType).value ??
                                    deriveLabel(option as OptionType);
                                const selectedKey =
                                    (selected as OptionType).id ??
                                    (selected as OptionType).value ??
                                    deriveLabel(selected as OptionType);
                                return optionKey === selectedKey;
                            }}
                            onBlur={onBlur}
                            onChange={(event, option: OptionType | null) => {
                                if (option == null) {
                                    onChange(null);
                                    return;
                                }
                                const chosenValue = option.id ?? option.value ?? null;
                                onChange(chosenValue);
                            }}
                            options={options ?? []}
                            ref={ref}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    error={Boolean(error?.message)}
                                    helperText={error?.message ?? helperText}
                                    required={required}
                                    variant="outlined"
                                />
                            )}
                            size={small ? 'small' : 'medium'}
                            sx={{ width: fullWidth ? undefined : 350 }}
                            value={
                                options?.find((opt) => {
                                    const optKey = (opt as OptionType).id ?? (opt as OptionType).value ?? null;
                                    return optKey === inputValue;
                                }) || null
                            }
                        />
                    </Box>
                );
            }}
            rules={{
                required: required ? `${label ?? value} cannot be empty` : false,
            }}
        />
    );
}
